import { useEffect } from "react"
import { API } from "aws-amplify"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
const CORE = "CORE"

const fetchExpenses = async (organization_id, projectId) => {
  let params = `/expenses?organizationId=${organization_id}`
  if (projectId) {
    params += `&projectId=${projectId}`
  }
  const res = await API.get(CORE, params)
  return res
}

export const useOrganizationExpenses = (
  organization_id,
  projectId,
  enabled = false,
) => {
  return useQuery({
    queryKey: ["organization-expenses", organization_id, projectId],
    queryFn: () => fetchExpenses(organization_id, projectId),
    enabled,
  })
}

const fetchExpense = async ({ organizationId, expenseId }) => {
  if (!organizationId || !expenseId)
    throw new Error("organizationId and expenseId are required")
  const params = `/expenses/single_expense?organizationId=${organizationId}&expenseId=${expenseId}`
  try {
    const response = await API.get(CORE, params)
    return response
  } catch (error) {
    console.error("Error in fetchExpense:", error)
    throw error
  }
}

export const useExpense = (organizationId, expenseId, enabled = true) => {
  return useQuery({
    queryKey: ["single-expense", organizationId, expenseId],
    queryFn: () => fetchExpense({ organizationId, expenseId }),
    enabled: !!organizationId && !!expenseId && enabled,
  })
}

const fetchSaveExpenses = async expenses => {
  try {
    const response = await API.post(CORE, "/expenses/save", { body: expenses })
    return response
  } catch (error) {
    console.error("Error in fetchSaveExpenses:", error)
    throw error
  }
}

export const useSaveExpenses = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ expenses }) => fetchSaveExpenses(expenses),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["organization-expenses"] })
    },
  })
}

const fetchUploadExpenseFiles = async ({
  organizationId,
  expenseId,
  files,
  createdByProject,
}) => {
  const formData = new FormData()
  files.forEach(file => {
    formData.append("files", file)
  })

  let url = `/expenses/upload_files?organizationId=${organizationId}&expenseId=${expenseId}`
  if (createdByProject !== undefined && createdByProject !== null) {
    url += `&createdByProject=${encodeURIComponent(createdByProject)}`
  }
  const response = await API.post("CORE", url, {
    body: formData,
  })
  return response
}

export const uploadExpenseFiles = async params => {
  try {
    return await fetchUploadExpenseFiles(params)
  } catch (error) {
    console.error("Error en uploadExpenseFiles:", error)
    console.error("Error in uploadExpenseFiles:", error)
    throw error
  }
}

const fetchDeleteExpenseFile = async ({
  organizationId,
  expenseId,
  fileId,
}) => {
  const response = await API.del(
    CORE,
    `/expenses/delete_file?organizationId=${organizationId}&expenseId=${expenseId}&fileId=${fileId}`,
  )
  return response
}

export const useDeleteExpenseFile = () => {
  const queryClient = useQueryClient()
  return async params => {
    try {
      const response = await fetchDeleteExpenseFile(params)
      console.log("Archivo eliminado:", response)
      queryClient.invalidateQueries({ queryKey: ["organization-expenses"] })
      return response
    } catch (error) {
      console.error("Error al eliminar archivo:", error)
      console.error("Error deleting file:", error)
      throw error
    }
  }
}

const fetchDeleteExpense = async ({ organizationId, expenseId }) => {
  const response = await API.del(
    CORE,
    `/expenses/delete_expense?organizationId=${organizationId}&expenseId=${expenseId}`,
  )
  return response
}

export const useDeleteExpense = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async params => {
      return await fetchDeleteExpense(params)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organization-expenses"] })
    },
  })
}

const fetchProjectsHeaders = async organization_id => {
  const res = await API.post(
    CORE,
    `/projects/get_from_organizationId/?organizationId=${organization_id}`,
  )
  return res
}

export const useProjectsHeaders = organization_id => {
  return useQuery({
    queryKey: ["projects-headers", organization_id],
    queryFn: () => fetchProjectsHeaders(organization_id),
  })
}

const fetchProjects = async projectIds => {
  if (!projectIds || !Array.isArray(projectIds)) {
    return []
  }

  const filteredIds = projectIds.filter(id => id !== null && id !== undefined)
  if (filteredIds.length === 0) {
    return []
  }
  const response = await API.post(CORE, "/projects/get_from_projectIds/", {
    body: { projectIds: filteredIds },
  })
  console.log("fetchProjects result:", response)
  return response
}

export const usePrefetchProjects = projectIds => {
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!Array.isArray(projectIds) || projectIds.length === 0) return
    const ids = Array.from(new Set(projectIds.filter(Boolean)))
    if (ids.length === 0) return

    ids.forEach(projectId => {
      if (queryClient.getQueryData(["projects", projectId])) return
      queryClient
        .prefetchQuery({
          queryKey: ["projects", projectId],
          queryFn: () => fetchProjects([projectId]),
        })
        .catch(err => {
          console.error("Error prefetching project", projectId, err)
        })
    })
  }, [projectIds, queryClient])
}

export const useProject = projectId => {
  return useQuery({
    queryKey: ["projects", projectId],
    queryFn: () => fetchProjects([projectId]),
  })
}

const fetchAllocateExpense = async imputations => {
  try {
    const response = await API.post(CORE, "/expenses/save_imputations", {
      body: imputations,
    })
    return response
  } catch (error) {
    console.error("🚀 Error in fetchAllocateExpense:", error)
    console.error("Error in fetchAllocateExpense:", error)
    throw error
  }
}

export const useAllocateExpense = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ imputations }) => fetchAllocateExpense(imputations),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["organization-expenses"] })
    },
    onError: (error, variables, context) => {
      console.error("🚀 Error allocating expense:", error)
      console.error("Error allocating expense:", error)
    },
  })
}
